import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Play, Clock, Search, Filter, Plus, Pencil, Trash2, Video, Upload, Youtube, Image } from 'lucide-react';

const API_URL = window.location.origin;

const TIER_OPTIONS = [
  { value: 'inner_circle', label: 'Inner Circle' },
  { value: 'inner_edge', label: 'Inner Edge' },
  { value: 'costa_rica', label: 'Costa Rica Retreat' },
  { value: 'coaching_activation', label: 'Signature Coaching' },
  { value: 'sdc', label: 'SDC VIP' },
];

const VIDEO_TYPE_OPTIONS = [
  { value: 'vimeo', label: 'Vimeo' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'upload', label: 'Upload Video File' },
];

const VideoLibraryPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Admin dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_type: 'vimeo',
    vimeo_url: '',
    youtube_url: '',
    video_file_url: '',
    thumbnail_url: '',
    duration: '00:00',
    category: '',
    instructor: 'Crystal & Dr. Nicole',
    required_tier: 'inner_circle'
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    fetchVideos();
    fetchCategories();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/videos/categories/list`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const filteredVideos = videos.filter(video => {
    const matchesSearch = search === '' || 
      video.title.toLowerCase().includes(search.toLowerCase()) ||
      video.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || video.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Admin functions
  const openAddDialog = () => {
    setEditingVideo(null);
    setFormData({
      title: '',
      description: '',
      video_type: 'vimeo',
      vimeo_url: '',
      youtube_url: '',
      video_file_url: '',
      thumbnail_url: '',
      duration: '00:00',
      category: categories[0] || 'Inner Circle Library',
      instructor: 'Crystal & Dr. Nicole',
      required_tier: 'inner_circle'
    });
    setUploadProgress(0);
    setShowDialog(true);
  };

  const openEditDialog = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description,
      video_type: video.video_type || 'vimeo',
      vimeo_url: video.vimeo_url || (video.vimeo_id ? `https://vimeo.com/${video.vimeo_id}` : ''),
      youtube_url: video.youtube_url || '',
      video_file_url: video.video_file_url || '',
      thumbnail_url: video.thumbnail_url || '',
      duration: video.duration || '00:00',
      category: video.category,
      instructor: video.instructor || 'Crystal & Dr. Nicole',
      required_tier: video.required_tier || 'inner_circle'
    });
    setUploadProgress(0);
    setShowDialog(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload MP4, WebM, MOV, or AVI.');
      return;
    }

    // Validate file size (500MB max)
    if (file.size > 500 * 1024 * 1024) {
      toast.error('File too large. Maximum 500MB allowed.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/api/admin/library/upload-video`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      setFormData(prev => ({
        ...prev,
        video_file_url: response.data.video_url,
        video_type: 'upload'
      }));
      toast.success('Video uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const handleThumbnailUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Thumbnail image must be less than 10MB');
      return;
    }

    setUploadingThumbnail(true);

    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/api/admin/library/upload-thumbnail`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData(prev => ({
        ...prev,
        thumbnail_url: response.data.thumbnail_url
      }));
      toast.success('Thumbnail uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload thumbnail');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Title and description are required');
      return;
    }

    // Validate video source based on type
    if (formData.video_type === 'vimeo' && !formData.vimeo_url) {
      toast.error('Please provide a Vimeo URL');
      return;
    }
    if (formData.video_type === 'youtube' && !formData.youtube_url) {
      toast.error('Please provide a YouTube URL');
      return;
    }
    if (formData.video_type === 'upload' && !formData.video_file_url) {
      toast.error('Please upload a video file');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editingVideo) {
        // Update existing video - only send non-empty thumbnail_url to preserve existing
        const updateData = { ...formData };
        if (!updateData.thumbnail_url || !updateData.thumbnail_url.trim()) {
          delete updateData.thumbnail_url; // Don't send empty thumbnail to avoid overwriting
        }
        await axios.put(`${API_URL}/api/admin/library/${editingVideo.id}`, updateData, { headers });
        toast.success('Video updated successfully');
      } else {
        // Create new video
        await axios.post(`${API_URL}/api/admin/library`, formData, { headers });
        toast.success('Video created successfully');
      }

      setShowDialog(false);
      fetchVideos();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save video');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (video) => {
    if (!window.confirm(`Are you sure you want to delete "${video.title}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/library/${video.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Video deleted successfully');
      fetchVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete video');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="video-library-page">
        {/* Banner */}
        <div className="mb-8 rounded-2xl overflow-hidden shadow-lg relative h-40 md:h-48" style={{background: 'linear-gradient(135deg, #2D4739 0%, #2D4739 60%, #C4704B 100%)'}}>
          <div className="absolute inset-0 flex items-center justify-between px-6 md:px-10">
            <div>
              <p className="text-white text-sm md:text-base font-medium opacity-90">The Vault</p>
              <p className="text-white text-xl md:text-2xl font-heading font-bold">Soul-Centered Content for Your Becoming</p>
              <p className="text-white text-sm mt-1 opacity-80">Courses, trainings & resources</p>
            </div>
            <Video className="h-16 w-16 md:h-20 md:w-20 text-white opacity-20" />
          </div>
        </div>
        
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold text-forest">
              The Vault
            </h1>
            <p className="text-gray-600 mt-2">
              Access our comprehensive collection of soul-centered content and training materials.
            </p>
          </div>
          {isAdmin && (
            <Button className="btn-primary" onClick={openAddDialog} data-testid="add-video-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Video
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search videos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 input-base"
              data-testid="video-search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              className={selectedCategory === 'all' ? 'btn-primary' : 'btn-outline'}
              onClick={() => setSelectedCategory('all')}
            >
              All
            </Button>
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                className={selectedCategory === category ? 'btn-primary' : 'btn-outline'}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Videos Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="card-base animate-pulse">
                <div className="aspect-video bg-sand rounded-lg mb-4" />
                <div className="h-6 bg-sand rounded w-3/4 mb-2" />
                <div className="h-4 bg-sand rounded w-full" />
              </div>
            ))}
          </div>
        ) : filteredVideos.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No videos found matching your search.</p>
            <Button 
              variant="outline" 
              className="mt-4 btn-outline"
              onClick={() => { setSearch(''); setSelectedCategory('all'); }}
            >
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVideos.map((video) => (
              <Card key={video.id} className="card-base overflow-hidden group" data-testid={`video-card-${video.id}`}>
                <Link to={`/videos/${video.id}`}>
                  <div className="video-card relative">
                    <img 
                      src={video.thumbnail_url} 
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="h-16 w-16 rounded-full bg-white/90 flex items-center justify-center">
                        <Play className="h-8 w-8 text-forest ml-1" />
                      </div>
                    </div>
                    <div className="absolute bottom-3 right-3 bg-black/70 text-white text-sm px-2 py-1 rounded flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {video.duration}
                    </div>
                  </div>
                </Link>
                
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <span className="inline-block px-2 py-1 bg-sand text-forest text-xs rounded-full mb-2">
                        {video.category}
                      </span>
                      <Link to={`/videos/${video.id}`}>
                        <h3 className="font-heading text-lg font-semibold text-forest line-clamp-2 group-hover:text-terracotta transition-colors">
                          {video.title}
                        </h3>
                      </Link>
                      <p className="text-gray-500 text-sm mt-1">with {video.instructor}</p>
                      <p className="text-gray-600 text-sm mt-2 line-clamp-2">{video.description}</p>
                    </div>
                    
                    {/* Admin Edit/Delete buttons */}
                    {isAdmin && (
                      <div className="flex flex-col gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={(e) => { e.preventDefault(); openEditDialog(video); }}
                          data-testid={`edit-video-${video.id}`}
                        >
                          <Pencil className="h-4 w-4 text-gray-500 hover:text-forest" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={(e) => { e.preventDefault(); handleDelete(video); }}
                          data-testid={`delete-video-${video.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-gray-500 hover:text-red-500" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Video Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest">
                {editingVideo ? 'Edit Video' : 'Add New Video'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter video title"
                  className="input-base"
                  data-testid="video-title-input"
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter video description"
                  className="input-base min-h-20"
                  data-testid="video-description-input"
                />
              </div>

              {/* Video Source Type */}
              <div>
                <Label>Video Source *</Label>
                <Select 
                  value={formData.video_type} 
                  onValueChange={(value) => setFormData({ ...formData, video_type: value })}
                >
                  <SelectTrigger className="input-base" data-testid="video-type-select">
                    <SelectValue placeholder="Select video source" />
                  </SelectTrigger>
                  <SelectContent>
                    {VIDEO_TYPE_OPTIONS.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Vimeo URL - show when video_type is 'vimeo' */}
              {formData.video_type === 'vimeo' && (
                <div>
                  <Label htmlFor="vimeo_url">Vimeo URL *</Label>
                  <Input
                    id="vimeo_url"
                    value={formData.vimeo_url}
                    onChange={(e) => setFormData({ ...formData, vimeo_url: e.target.value })}
                    placeholder="https://vimeo.com/123456789"
                    className="input-base"
                    data-testid="video-vimeo-input"
                  />
                </div>
              )}

              {/* YouTube URL - show when video_type is 'youtube' */}
              {formData.video_type === 'youtube' && (
                <div>
                  <Label htmlFor="youtube_url">YouTube URL *</Label>
                  <div className="flex items-center gap-2">
                    <Youtube className="h-5 w-5 text-red-500" />
                    <Input
                      id="youtube_url"
                      value={formData.youtube_url}
                      onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                      placeholder="https://youtube.com/watch?v=... or https://youtu.be/..."
                      className="input-base flex-1"
                      data-testid="video-youtube-input"
                    />
                  </div>
                </div>
              )}

              {/* Video Upload - show when video_type is 'upload' */}
              {formData.video_type === 'upload' && (
                <div>
                  <Label>Upload Video File *</Label>
                  <div className="space-y-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full btn-outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <>Uploading... {uploadProgress}%</>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          {formData.video_file_url ? 'Change Video File' : 'Select Video File'}
                        </>
                      )}
                    </Button>
                    {uploading && (
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-terracotta h-2 rounded-full transition-all" 
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    )}
                    {formData.video_file_url && !uploading && (
                      <p className="text-sm text-green-600 flex items-center gap-1">
                        <Video className="h-4 w-4" />
                        Video uploaded: {formData.video_file_url.split('/').pop()}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      Supported formats: MP4, WebM, MOV, AVI (max 500MB)
                    </p>
                  </div>
                </div>
              )}
              
              <div>
                <Label>Thumbnail</Label>
                <div className="space-y-3">
                  {/* Thumbnail Preview */}
                  {formData.thumbnail_url && (
                    <div className="relative w-40 h-24 rounded-lg overflow-hidden border">
                      <img 
                        src={formData.thumbnail_url.startsWith('/api') ? `${API_URL}${formData.thumbnail_url}` : formData.thumbnail_url}
                        alt="Thumbnail preview"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  
                  {/* Upload thumbnail button */}
                  <div className="flex gap-2">
                    <input
                      ref={thumbnailInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={handleThumbnailUpload}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => thumbnailInputRef.current?.click()}
                      disabled={uploadingThumbnail}
                      className="flex-shrink-0"
                    >
                      {uploadingThumbnail ? (
                        <span className="animate-spin mr-2">⏳</span>
                      ) : (
                        <Image className="h-4 w-4 mr-2" />
                      )}
                      {uploadingThumbnail ? 'Uploading...' : 'Upload Image'}
                    </Button>
                    <span className="text-gray-400 self-center">or</span>
                    <Input
                      id="thumbnail_url"
                      value={formData.thumbnail_url}
                      onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                      placeholder="Paste image URL"
                      className="input-base flex-1"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Upload an image (JPEG, PNG, WebP, GIF - max 10MB) or paste a URL
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="duration">Duration</Label>
                  <Input
                    id="duration"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    placeholder="00:00"
                    className="input-base"
                  />
                </div>
                
                <div>
                  <Label htmlFor="instructor">Instructor</Label>
                  <Input
                    id="instructor"
                    value={formData.instructor}
                    onChange={(e) => setFormData({ ...formData, instructor: e.target.value })}
                    placeholder="Crystal & Dr. Nicole"
                    className="input-base"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g., Inner Circle Library"
                  className="input-base"
                />
              </div>
              
              <div>
                <Label htmlFor="required_tier">Required Tier</Label>
                <Select 
                  value={formData.required_tier} 
                  onValueChange={(value) => setFormData({ ...formData, required_tier: value })}
                >
                  <SelectTrigger className="input-base" data-testid="video-tier-select">
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIER_OPTIONS.map((tier) => (
                      <SelectItem key={tier.value} value={tier.value}>
                        {tier.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button 
                className="btn-primary" 
                onClick={handleSave}
                disabled={saving}
                data-testid="save-video-btn"
              >
                {saving ? 'Saving...' : (editingVideo ? 'Update Video' : 'Add Video')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default VideoLibraryPage;
